import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { appAuthApi } from '../api/appAuthApi';
import { ApiError, isSupplierAuthPath } from '../api/client';

const AppAuthContext = createContext();

export const useAppAuth = () => {
  const context = useContext(AppAuthContext);
  if (!context) {
    throw new Error('useAppAuth must be used within AppAuthProvider');
  }
  return context;
};

export const AppAuthProvider = ({ children }) => {
  const [sessionToken, setSessionToken] = useState(
    () => localStorage.getItem('app_session_token') || null
  );
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem('app_user');
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(true);

  const persist = (token, userInfo) => {
    setSessionToken(token);
    setUser(userInfo);
    if (token) {
      localStorage.setItem('app_session_token', token);
    } else {
      localStorage.removeItem('app_session_token');
    }
    if (userInfo) {
      localStorage.setItem('app_user', JSON.stringify(userInfo));
    } else {
      localStorage.removeItem('app_user');
    }
  };

  const refreshMe = useCallback(async () => {
    if (!sessionToken) {
      setLoading(false);
      return null;
    }
    // 仕入先タブでは社内 me を叩かない（未送信 401 で共有 localStorage の社内セッションを消さない）
    if (isSupplierAuthPath()) {
      setLoading(false);
      return null;
    }
    try {
      const me = await appAuthApi.me();
      const userInfo = {
        shain_code: me.shain_code,
        login_id: me.login_id,
        display_name: me.display_name,
        role: me.role,
        must_change_password: me.must_change_password,
      };
      setUser(userInfo);
      localStorage.setItem('app_user', JSON.stringify(userInfo));
      return userInfo;
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.status === 401 &&
        !isSupplierAuthPath()
      ) {
        persist(null, null);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = async (loginId, password) => {
    const res = await appAuthApi.login(loginId, password);
    const userInfo = {
      shain_code: res.shain_code,
      login_id: res.login_id,
      display_name: res.display_name,
      role: res.role,
      must_change_password: res.must_change_password,
    };
    persist(res.session_token, userInfo);
    return res;
  };

  const logout = async () => {
    try {
      if (sessionToken) {
        await appAuthApi.logout();
      }
    } catch {
      // ignore
    }
    persist(null, null);
  };

  const isAuthenticated = !!sessionToken && !!user;
  const isAdmin = user?.role === 'admin';

  return (
    <AppAuthContext.Provider
      value={{
        sessionToken,
        user,
        loading,
        isAuthenticated,
        isAdmin,
        login,
        logout,
        refreshMe,
      }}
    >
      {children}
    </AppAuthContext.Provider>
  );
};
